import React from "react";
import { Link } from "react-router-dom";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/data/seed";
import { useApp } from "@/context/AppContext";
import { imgSrc } from "@/lib/api";

const CAPABILITIES = [
  "Client Understanding", "Market Intelligence", "Micro-Market Mapping", "Developer Relationships",
  "Property Evaluation", "Opportunity Curation", "Negotiation", "Transaction Support",
];

const TeamCard = ({ m }) => (
  <div className="arch-card h-full overflow-hidden" data-testid={`team-member-${m.id}`}>
    {m.photo ? (
      <div className="img-zoom aspect-[4/5] overflow-hidden"><img src={imgSrc(m.photo)} alt={m.name} className="h-full w-full object-cover" /></div>
    ) : (
      <div className="flex aspect-[4/5] items-center justify-center bg-[var(--bg-primary)]">
        <div className="flex h-20 w-20 items-center justify-center rounded-full font-serif text-2xl text-[#071711]" style={{ background: "linear-gradient(135deg,#9ED3B8,#0F4A36)" }}>{m.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</div>
      </div>
    )}
    <div className="p-5">
      <h4 className="font-serif text-xl text-[var(--text-primary)]">{m.name}</h4>
      <div className="mt-0.5 text-xs font-medium uppercase tracking-widest text-[var(--accent-gold)]">{m.role}</div>
      {m.bio && <p className="mt-3 text-sm leading-relaxed text-[var(--text-muted)]">{m.bio}</p>}
    </div>
  </div>
);

export default function About() {
  const { data } = useApp();
  const team = data.team || [];
  const s = data.settings || {};
  return (
    <div>
      <section className="container-arch pt-32 pb-16">
        <Reveal>
          <div className="overline">About Archedia</div>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-[1.1] text-[var(--text-primary)] sm:text-5xl lg:text-6xl">
            Perspective Builds <span className="gold-gradient-text italic">Possibilities.</span>
          </h1>
        </Reveal>
      </section>

      <section className="container-arch grid gap-12 pb-20 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <p className="text-lg leading-relaxed text-[var(--text-primary)]" data-testid="about-line1">{s.aboutLine1 || "Most property journeys begin by showing clients projects."}</p>
          <p className="mt-4 text-lg leading-relaxed text-[var(--accent-gold)]">{s.aboutLine2 || "Archedia begins by understanding the client."}</p>
          <p className="mt-6 text-[var(--text-muted)]">
            {s.aboutBody1 || "We are not a property portal, a broker directory or a listing aggregator. We are a premium real-estate advisory and decision platform."}
          </p>
          <p className="mt-4 text-[var(--text-muted)]">
            {s.aboutBody2 || "The result is fewer options, better perspective and higher conviction — a genuine decision partner rather than an intermediary."}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="img-zoom overflow-hidden rounded-2xl"><img src={IMAGES.penthouse} alt="Archedia" className="h-full w-full object-cover" /></div>
        </Reveal>
      </section>

      <section className="border-y border-[var(--border-subtle)] bg-[var(--bg-card)] py-20 lg:py-28">
        <div className="container-arch">
          <Reveal><div className="overline">Capabilities</div><h2 className="mt-3 font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">A Real Estate Decision Partner</h2></Reveal>
          <Stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CAPABILITIES.map((c) => (
              <StaggerItem key={c}>
                <div className="arch-card h-full p-6">
                  <div className="h-8 w-8 rounded-full" style={{ background: "linear-gradient(135deg,#9ED3B8,#0F4A36)" }} />
                  <h4 className="mt-4 font-serif text-lg text-[var(--text-primary)]">{c}</h4>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {team.length > 0 && (
        <section className="container-arch py-20 lg:py-28" data-testid="about-team-section">
          <Reveal>
            <div className="overline">The People</div>
            <h2 className="mt-3 max-w-2xl font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">Perspective is built by people who understand it.</h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((m) => <StaggerItem key={m.id}><TeamCard m={m} /></StaggerItem>)}
          </Stagger>
        </section>
      )}

      <section className="container-arch py-24 text-center">
        <Reveal>
          <p className="mx-auto max-w-2xl font-serif text-2xl font-light leading-snug text-[var(--text-primary)] sm:text-3xl">
            A visitor should understand how Archedia thinks before ever speaking with an advisor.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/request" className="btn-gold">Find My Property <ArrowUpRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="btn-outline-gold">Talk to Archedia</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
